import React from 'react';
import { Box, Container, Link as MUILink, Typography, useTheme, alpha } from '@mui/material';
import { Email, LocationOn, Phone } from '@mui/icons-material';
import { COMPANY } from '../../data/siteContent';
import { SECTION_SCROLL_MARGIN } from '../../utils/scroll';
import { MotionCard, fadeInUp, useMotionProps, useReducedMotion } from '../motion';
import SectionHeading from './SectionHeading';

const CHANNELS = [
  {
    icon: Email,
    title: 'Email Us',
    caption: 'Send us an email anytime',
    value: COMPANY.email,
    href: `mailto:${COMPANY.email}`
  },
  {
    icon: Phone,
    title: 'Call Us',
    caption: COMPANY.hours,
    value: COMPANY.phone,
    href: COMPANY.phoneHref
  },
  {
    icon: LocationOn,
    title: 'Visit Us',
    caption: 'Come visit our office',
    value: COMPANY.location,
    href: null
  }
];

const Contact = () => {
  const theme = useTheme();
  const motionProps = useMotionProps();
  const reducedMotion = useReducedMotion();

  return (
    <Box
      id="contact"
      component="section"
      aria-labelledby="contact-heading"
      sx={{
        scrollMarginTop: SECTION_SCROLL_MARGIN,
        py: { xs: 8, md: 12 },
        position: 'relative',
        backgroundColor: 'background.paper'
      }}
    >
      <Box aria-hidden sx={{ position: 'absolute', inset: 0, background: theme.surfaces.aurora, opacity: 0.6 }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <SectionHeading
          id="contact-heading"
          eyebrow="Contact"
          title="Get In Touch"
          subtitle="Have questions? We would love to hear from you. Contact us today to learn more about our solutions."
        />

        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2.5, md: 3 },
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
            alignItems: 'stretch'
          }}
        >
          {CHANNELS.map(({ icon: Icon, title, caption, value, href }, index) => (
            <MotionCard
              key={title}
              {...motionProps(fadeInUp(index * 0.08))}
              whileHover={reducedMotion ? undefined : { y: -6 }}
              sx={{
                p: 4,
                textAlign: 'center',
                height: '100%',
                borderRadius: 4,
                ...theme.surfaces.glass,
                borderColor: alpha(theme.palette.primary.main, 0.14),
                '&:hover': {
                  borderColor: alpha(theme.palette.primary.main, 0.32),
                  boxShadow: `0 18px 48px ${alpha(theme.palette.primary.main, 0.16)}`
                }
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                  mx: 'auto',
                  mb: 2
                }}
              >
                <Icon sx={{ fontSize: 28 }} />
              </Box>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {caption}
              </Typography>
              {href ? (
                <MUILink href={href} color="primary" sx={{ fontWeight: 600 }}>
                  {value}
                </MUILink>
              ) : (
                <Typography color="primary" sx={{ fontWeight: 600 }}>
                  {value}
                </Typography>
              )}
            </MotionCard>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default Contact;
